import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Wind, MapPin, ThumbsUp, ThumbsDown, AlertTriangle, Filter, RefreshCw } from 'lucide-react';
import { StenchReport, StenchLevel, StenchStatistics, createReport, getReports, voteOnReport, getStatistics } from './api/reports';

const App = () => {
  // State
  const [reports, setReports] = useState<StenchReport[]>([]);
  const [statistics, setStatistics] = useState<StenchStatistics | null>(null);
  const [newReport, setNewReport] = useState<{
    level: StenchLevel;
    location: string;
    description: string;
  }>({
    level: 3,
    location: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentLocation] = useState('Hyrum, Utah');
  const [filter, setFilter] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    location: '',
  });
  const [weatherData, setWeatherData] = useState({
    temperature: '',
    windDirection: '',
    windSpeed: '',
    humidity: ''
  });

  // Fetch reports and statistics
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch reports with any active filters
      const reportsData = await getReports(
        filter.startDate ? new Date(filter.startDate).toISOString() : undefined,
        filter.endDate ? new Date(filter.endDate + 'T23:59:59').toISOString() : undefined,
        filter.location || undefined
      );
      setReports(reportsData);

      // Fetch statistics for the same date range
      const statsData = await getStatistics(
        filter.startDate ? new Date(filter.startDate).toISOString() : undefined,
        filter.endDate ? new Date(filter.endDate + 'T23:59:59').toISOString() : undefined
      );
      setStatistics(statsData);

      // Extract weather data from the most recent report if available
      if (reportsData.length > 0) {
        const latestReport = reportsData[0];
        setWeatherData({
          temperature: latestReport.temperature || '',
          windDirection: latestReport.windDirection || '',
          windSpeed: latestReport.windSpeed || '',
          humidity: latestReport.humidity || ''
        });
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [filter.startDate, filter.endDate, filter.location]); // Include dependencies that fetchData relies on

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now fetchData is properly included in the dependency array

  // Handle report submission
  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Validate
    if (!newReport.location || !newReport.description) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Submit report to API
      await createReport(
        newReport.level,
        newReport.location,
        newReport.description
      );
      
      // Reset form
      setNewReport({
        level: 3,
        location: '',
        description: '',
      });
      
      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error submitting report:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle voting on a report
  const handleVote = async (reportId: string, voteType: 'upvote' | 'downvote') => {
    try {
      await voteOnReport(reportId, voteType);
      // Refresh data to get updated vote counts
      fetchData();
    } catch (err) {
      console.error('Error voting on report:', err);
      setError('Failed to register your vote. Please try again.');
    }
  };

  // Handle filter changes
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filters
  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    fetchData();
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      location: '',
    });
    // Fetch data with reset filters
    fetchData();
  };

  // Get stench level color
  const getStenchColor = (level: StenchLevel) => {
    switch(level) {
      case 1: return 'bg-green-100 text-green-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 3: return 'bg-yellow-100 text-yellow-800';
      case 4: return 'bg-orange-100 text-orange-800';
      case 5: return 'bg-red-100 text-red-800';
    }
  };

  // Format timestamp for display
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Emoji representation
  const getStenchEmoji = (level: StenchLevel) => {
    switch(level) {
      case 1: return '😊'; // Barely noticeable
      case 2: return '🙂'; // Slight
      case 3: return '😐'; // Moderate
      case 4: return '🤢'; // Strong
      case 5: return '🤮'; // Unbearable
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Wind size={28} />
              <h1 className="text-2xl font-bold">Hyrum Stench Tracker</h1>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin size={20} />
              <span>{currentLocation}</span>
            </div>
          </div>
          
          {/* Current weather info bar */}
          <div className="mt-4 bg-white/10 rounded-lg p-3 flex justify-between items-center text-sm">
            <div className="flex items-center space-x-1">
              <Calendar size={16} />
              <span>{new Date().toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock size={16} />
              <span>{new Date().toLocaleTimeString()}</span>
            </div>
            {weatherData.temperature && <div>Temp: {weatherData.temperature}</div>}
            {weatherData.windDirection && <div>Wind: {weatherData.windDirection} {weatherData.windSpeed}</div>}
            {weatherData.humidity && <div>Humidity: {weatherData.humidity}</div>}
            <button 
              onClick={fetchData}
              className="bg-white/20 p-1 rounded hover:bg-white/30 transition-colors"
              title="Refresh data"
            >
              <RefreshCw size={16} />
            </button>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="mt-2 bg-red-500/80 text-white p-2 rounded-lg">
              {error}
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Filter bar */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center mb-2">
            <Filter size={18} className="mr-2 text-indigo-600" />
            <h2 className="text-lg font-medium">Filter Reports</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filter.startDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filter.endDate}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location (optional)
              </label>
              <input
                type="text"
                name="location"
                value={filter.location}
                onChange={handleFilterChange}
                placeholder="E.g. Downtown"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            <div className="flex items-end space-x-2">
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                Apply Filters
              </button>
              
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Submit report card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <AlertTriangle size={20} className="mr-2 text-yellow-500" />
                Report a Stench
              </h2>
              
              <div>
                {/* Stench Level Slider */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How bad is it? ({newReport.level}/5)
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">😊</span>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      value={newReport.level}
                      onChange={(e) => setNewReport({...newReport, level: parseInt(e.target.value) as StenchLevel})}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-lg">🤮</span>
                  </div>
                </div>
                
                {/* Location */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specific Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Downtown, Near the park"
                    value={newReport.location}
                    onChange={(e) => setNewReport({...newReport, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Describe the smell... Is it industrial, agricultural, etc?"
                    value={newReport.description}
                    onChange={(e) => setNewReport({...newReport, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                {/* Submit button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition duration-200 flex justify-center items-center"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
            
            {/* Stats card */}
            <div className="mt-6 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Statistics</h2>
              {isLoading ? (
                <div className="flex justify-center p-4">
                  <div className="animate-spin h-6 w-6 border-2 border-indigo-500 rounded-full border-t-transparent"></div>
                </div>
              ) : statistics ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total reports:</span>
                    <span className="font-medium">{statistics.totalReports}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average level:</span>
                    <span className="font-medium">{statistics.averageLevel}/5</span>
                  </div>
                  {statistics.worstLocation && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Worst location:</span>
                      <span className="font-medium">{statistics.worstLocation}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t">
                    <div className="text-sm font-medium text-gray-700 mb-2">Reports by level:</div>
                    <div className="flex h-8">
                      {[1, 2, 3, 4, 5].map((level) => {
                        const count = statistics.reportsByLevel[level as StenchLevel];
                        const total = statistics.totalReports;
                        const percentage = total > 0 ? (count / total) * 100 : 0;
                        
                        return (
                          <div 
                            key={level}
                            style={{ width: `${percentage}%` }}
                            className={`h-full flex items-center justify-center text-xs text-white font-bold ${
                              level === 1 ? 'bg-green-500' :
                              level === 2 ? 'bg-blue-500' :
                              level === 3 ? 'bg-yellow-500' :
                              level === 4 ? 'bg-orange-500' :
                              'bg-red-500'
                            }`}
                            title={`Level ${level}: ${count} reports (${percentage.toFixed(1)}%)`}
                          >
                            {percentage > 10 ? `${percentage.toFixed(0)}%` : ''}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-gray-500">
                      <span>Mild</span>
                      <span>Severe</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  No statistics available
                </div>
              )}
            </div>
          </div>
          
          {/* Reports list */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Recent Reports</h2>
              
              {isLoading ? (
                <div className="flex justify-center p-12">
                  <div className="animate-spin h-8 w-8 border-4 border-indigo-500 rounded-full border-t-transparent"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.id} className="border rounded-lg p-4 hover:shadow-md transition duration-200">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">
                            {getStenchEmoji(report.level)}
                          </span>
                          <div>
                            <h3 className="font-medium">{report.location}</h3>
                            <p className="text-sm text-gray-500">{formatDate(report.timestamp)}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStenchColor(report.level)}`}>
                          Level {report.level}
                        </span>
                      </div>
                      
                      <p className="mt-2 text-gray-700">{report.description}</p>
                      
                      <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          Wind: {report.windDirection || 'N/A'} {report.windSpeed}
                        </div>
                        <div>
                          Temp: {report.temperature || 'N/A'}, Humidity: {report.humidity || 'N/A'}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="mt-3 flex space-x-4">
                        <button 
                          className="text-gray-500 hover:text-green-600 flex items-center space-x-1"
                          onClick={() => handleVote(report.id, 'upvote')}
                        >
                          <ThumbsUp size={16} />
                          <span>Agree {report.upvotes ? `(${report.upvotes})` : ''}</span>
                        </button>
                        <button 
                          className="text-gray-500 hover:text-red-600 flex items-center space-x-1"
                          onClick={() => handleVote(report.id, 'downvote')}
                        >
                          <ThumbsDown size={16} />
                          <span>Disagree {report.downvotes ? `(${report.downvotes})` : ''}</span>
                        </button>
                      </div>
                    </div>
                  ))}

                  {reports.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <Wind size={48} className="mx-auto mb-4 opacity-30" />
                      <p>No stench reports found for the selected filters.</p>
                      <button 
                        onClick={resetFilters}
                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        Reset Filters
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <footer className="mt-12 bg-gray-800 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p>© 2025 Hyrum Stench Tracker</p>
          <p className="text-gray-400 text-sm mt-1">
            Helping make Hyrum smell better, one report at a time
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
